import React from 'react';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';
import { aliasColors, brandColors, palette } from '../../../theme';

export type IconTextFieldColors = {
  field: string;
  focus: string;
  hover: string;
  icon: string;
  text: string;
  label: string;
  error: string;
};

export type IconTextFieldProps = {
  id: string;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  /** Visible label above the field. */
  label?: React.ReactNode;
  /** Icon shown at the start of the field, e.g. a mail icon. */
  icon?: React.ReactNode;
  /** Content at the end of the field, e.g. a "Change" link. */
  endAction?: React.ReactNode;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  placeholder?: string;
  name?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Shows the value as text the user cannot edit (e.g. the address a code was sent to). */
  readOnly?: boolean;
  error?: boolean;
  /** Message under the field. Space is kept for it so the layout does not jump. */
  helperText?: React.ReactNode;
  reserveHelperSpace?: boolean;
  colors?: Partial<IconTextFieldColors>;
  sx?: SxProps<Theme>;
};

const DEFAULT_COLORS: IconTextFieldColors = {
  field: aliasColors.background.bg03,
  focus: brandColors.primary.yovoRed400,
  hover: brandColors.neutrals.grey300,
  icon: brandColors.neutrals.grey600,
  text: brandColors.neutrals.base1000,
  label: brandColors.neutrals.base1000,
  error: palette.error.main,
};

/**
 * Filled text field with a leading icon and an optional trailing action.
 * Label sits above the field; the field has no visible border until hover or focus.
 */
function IconTextField({
  id,
  value,
  onChange,
  onBlur,
  label,
  icon,
  endAction,
  type = 'text',
  inputMode,
  autoComplete,
  placeholder,
  name,
  autoFocus,
  disabled,
  readOnly = false,
  error = false,
  helperText,
  reserveHelperSpace = true,
  colors: colorOverrides,
  sx,
}: IconTextFieldProps) {
  const colors = { ...DEFAULT_COLORS, ...colorOverrides };
  const helper = helperText || (reserveHelperSpace ? ' ' : undefined);

  return (
    <Box sx={sx}>
      {label ? (
        <Box
          component="label"
          htmlFor={id}
          sx={{ display: 'block', mb: 1, fontSize: 13, fontWeight: 600, color: colors.label }}
        >
          {label}
        </Box>
      ) : null}
      <TextField
        id={id}
        name={name}
        fullWidth
        type={type}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        error={error}
        helperText={helper}
        onChange={(event) => onChange?.(event.target.value)}
        onBlur={onBlur}
        slotProps={{
          input: {
            readOnly,
            startAdornment: icon ? (
              <InputAdornment position="start" sx={{ color: error ? colors.error : colors.icon, mr: 0.5 }}>
                {icon}
              </InputAdornment>
            ) : undefined,
            endAdornment: endAction ? <InputAdornment position="end">{endAction}</InputAdornment> : undefined,
          },
          htmlInput: { inputMode, autoComplete, 'aria-invalid': error || undefined },
          formHelperText: { sx: { mx: 0, color: error ? colors.error : undefined } },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: colors.field,
            color: colors.text,
            transition: 'background-color 150ms ease',
            '& fieldset': { borderColor: 'transparent', borderWidth: '1.5px' },
            '&:hover fieldset': { borderColor: readOnly ? 'transparent' : colors.hover },
            '&.Mui-focused fieldset': { borderColor: readOnly ? 'transparent' : colors.focus, borderWidth: '1.5px' },
            '&.Mui-focused': { backgroundColor: readOnly ? colors.field : '#FFFFFF' },
            '&.Mui-error fieldset': { borderColor: colors.error },
          },
          '& .MuiOutlinedInput-input': {
            py: 1.6,
            fontSize: 15,
            textOverflow: 'ellipsis',
            cursor: readOnly ? 'default' : undefined,
          },
        }}
      />
    </Box>
  );
}

export default IconTextField;
